import clsx from 'clsx';
import PostItem from 'components/Pages/Blog/PostItem';
import ClinicSliderItem from 'components/Pages/LocationPage/ClinicSliderItem';
import GenericCTASmall from 'components/ui/ContentBlocks/GenericCTASmall';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import config from 'config';
import { useVerticalSwipePreventer } from 'hooks/useVerticalSwipePreventer';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import tailwind, { findViewport } from 'utils/breakpoints';
import { throttle } from 'utils/throttle';
import type { BlogItem } from 'types/Blog';
import type { ClinicPage } from 'types/Clinic';
import type { RecentStoriesItem } from 'types/PageData';

const sliderContentMapping = {
  postItem: {
    classes: {
      showAfter: '',
      showBefore: '',
      arrows: 'top-0 mt-20',
    },
    config: {
      threshold: 3,
      responsive: {
        superLargeDesktop: {
          breakpoint: { max: 4000, min: 3000 },
          items: 3,
          partialVisibilityGutter: 40,
        },
        desktop: {
          breakpoint: { max: 3000, min: 1024 },
          items: 3,
          partialVisibilityGutter: 40,
        },
        tablet: {
          breakpoint: { max: 1024, min: 768 },
          items: 2,
          partialVisibilityGutter: 40,
        },
        mobile: {
          breakpoint: { max: 768, min: 0 },
          items: 1,
          partialVisibilityGutter: 60,
        },
      },
      desktopSingleItemWidth: 'w-1/3',
    },
  },
  clinicItem: {
    classes: {
      showAfter: '',
      showBefore: '',
      arrows: '',
    },
    config: {
      threshold: 2,
      responsive: {
        superLargeDesktop: {
          breakpoint: { max: 4000, min: 3001 },
          items: 4,
          partialVisibilityGutter: 40,
        },
        desktop: {
          breakpoint: { max: 3000, min: 1025 },
          items: 4,
          partialVisibilityGutter: 40,
        },
        tablet: {
          breakpoint: { max: 1024, min: 769 },
          items: 2,
          partialVisibilityGutter: 40,
        },
        mobile: {
          breakpoint: { max: 768, min: 0 },
          items: 1,
          partialVisibilityGutter: 60,
        },
      },
      desktopSingleItemWidth: 'w-1/4',
    },
  },
};

type Props = {
  blockId?: string;
  type: 'postItem' | 'clinicItem';
  items?: RecentStoriesItem[];
  title?: string;
};

const Slider = ({
  blockId,
  type,
  items: _items = [],
  title,
}: Props): JSX.Element => {
  const containerRef = useRef(null);
  const [isPartialVisible, setIsPartialVisible] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useVerticalSwipePreventer(containerRef);

  const sliderContent = sliderContentMapping[type];

  const configOrDefault =
    sliderContent.config || config.defaults.treatmentsCardSlider;

  const checkViewport = useCallback((newWidth: number) => {
    const viewportName = findViewport(newWidth);
    if (['lg', 'xl'].includes(viewportName)) {
      setIsPartialVisible(false);
      setIsSmallScreen(false);
    }

    if (['', 'xs', 'sm', 'md'].includes(viewportName)) {
      setIsPartialVisible(true);
      setIsSmallScreen(true);
    }
  }, []);

  useEffect(() => {
    checkViewport(window.innerWidth);

    const throttledResizeListener = throttle(
      () => checkViewport(window.innerWidth),
      200,
    );

    window.addEventListener('resize', throttledResizeListener, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', throttledResizeListener);
    };
  }, []);

  const renderSliderItem = (
    item: RecentStoriesItem,
    className: string,
  ): JSX.Element => {
    if (type === 'postItem') {
      return <PostItem className={className} fields={item as BlogItem} />;
    }

    return (
      <ClinicSliderItem className={className} fields={item as ClinicPage} />
    );
  };

  const items = _items.filter(Boolean);

  return (
    <div
      ref={containerRef}
      id={blockId}
      className={clsx(
        configOrDefault.threshold <= items.length && '-mx-6',
        'lg:mx-0 mb-8 relative w-full',
      )}
    >
      {title && <h2 className="text-primary text-3xl mb-10 mx-3">{title}</h2>}
      {configOrDefault.threshold < items.length ||
      (configOrDefault.threshold <= items.length && isSmallScreen) ? (
        <Carousel
          responsive={configOrDefault.responsive}
          arrows
          partialVisible={isPartialVisible}
          customLeftArrow={
            <CustomSliderArrow
              className={clsx(
                'bg-white rounded-full h-8 w-8 p-2 left-0 -ml-0',
                sliderContent.classes.arrows,
              )}
              direction="left"
              color={tailwind.theme.backgroundColor.navy}
              style={{ boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)' }}
            />
          }
          customRightArrow={
            <CustomSliderArrow
              className={clsx(
                'bg-white rounded-full h-8 w-8 p-2 right-0 -mr-0',
                sliderContent.classes.arrows,
              )}
              direction="right"
              color={tailwind.theme.backgroundColor.navy}
              style={{ boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)' }}
            />
          }
          removeArrowOnDeviceType={['mobile']}
          containerClass="flex relative items-center lg:overflow-hidden px-6 lg:px-0"
          sliderClass="flex relative flex-row list-none p-0 m-0"
        >
          {items.map((item, i) => {
            if ('isCta' in item && item.isCta) {
              return <GenericCTASmall key={i} {...item.fields} />;
            }

            return (
              <Fragment key={i}>
                {renderSliderItem(item, 'h-full mx-1 md:mx-3')}
              </Fragment>
            );
          })}
        </Carousel>
      ) : (
        <div className="sm:flex -mx-3">
          {items.map((item, i) => {
            if ('isCta' in item && item.isCta) {
              return (
                <GenericCTASmall
                  key={i}
                  {...item.fields}
                  className={clsx(
                    'mx-3',
                    sliderContent.config.desktopSingleItemWidth,
                  )}
                />
              );
            }

            return (
              <Fragment key={i}>
                {renderSliderItem(
                  item,
                  clsx(
                    'mx-3',
                    items.length === 1 && isSmallScreen
                      ? 'w-full'
                      : sliderContent.config.desktopSingleItemWidth,
                  ),
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Slider;
